import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from '@/lib/core/utils';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BusinessDetails } from '@/types/index';
import { GoogleReviewCard } from './GoogleReviewCard';

// Define interfaces for the expected data structures
interface TimeDataPoint {
  date: string;
  positive: number;
  negative: number;
  neutral: number;
}

interface SentimentDataPoint {
  name: "Positive" | "Neutral" | "Negative";
  value: number;
  color: string;
}

interface SourceDataPoint {
  name: string;
  value: number;
  color: string;
}

interface ReviewInsightsProps {
  reviewsOverTimeData: TimeDataPoint[];
  sentimentAnalysisData: SentimentDataPoint[];
  reviewSourcesData: SourceDataPoint[];
  averageRating: number | null;
  totalReviews: number | null;
  starRatingDistribution: { rating: number; count: number }[];
  trend: 'up' | 'down' | 'neutral' | null;
  businessDetails?: BusinessDetails | null;
}

// Default empty data to prevent errors if props are not yet loaded
const defaultTimeData: TimeDataPoint[] = [];
const defaultSentimentData: SentimentDataPoint[] = [];
const defaultSourceData: SourceDataPoint[] = [];
const defaultAverageRating: number | null = null;
const defaultTotalReviews: number | null = null;
const defaultStarRatingDistribution: { rating: number; count: number }[] = [];
const defaultTrend: 'up' | 'down' | 'neutral' | null = null;

const ReviewInsights: React.FC<ReviewInsightsProps> = ({
  reviewsOverTimeData = defaultTimeData,
  sentimentAnalysisData = defaultSentimentData,
  reviewSourcesData = defaultSourceData,
  averageRating = defaultAverageRating,
  totalReviews = defaultTotalReviews,
  starRatingDistribution = defaultStarRatingDistribution,
  trend = defaultTrend,
  businessDetails = null,
}) => {

  const formatDateForChart = (dateString: string) => {
    const date = new Date(dateString);
    // Format as Month Day (e.g., Jun 17) for daily granularity
    return format(date, 'MMM dd');
  };

  // Basic check if data is available
  const hasData = reviewsOverTimeData.length > 0 || sentimentAnalysisData.length > 0 || reviewSourcesData.length > 0 || averageRating !== null || totalReviews !== null || starRatingDistribution.length > 0 || trend !== null;

  if (!hasData) {
    return (
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Reviews Over Time</CardTitle>
          </CardHeader>
          <CardContent className="h-80 flex items-center justify-center">
            <p className="text-gray-500">No analytics data available yet.</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
          </CardHeader>
          <CardContent className="h-64 flex items-center justify-center">
            <p className="text-gray-500">No rating data available yet.</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Sentiment Analysis</CardTitle>
          </CardHeader>
          <CardContent className="h-64 flex items-center justify-center">
            <p className="text-gray-500">No analytics data available yet.</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Review Sources</CardTitle>
          </CardHeader>
          <CardContent className="h-64 flex items-center justify-center">
            <p className="text-gray-500">No analytics data available yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 overflow-x-hidden">
      {/* Filter controls are now in FilterControls component */}

      {/* Reviews Over Time - Full Width */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Reviews Over Time</CardTitle>
          <CardDescription>Monthly breakdown of reviews by sentiment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={reviewsOverTimeData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDateForChart} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="positive" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} name="Positive" dot={true} />
                <Area type="monotone" dataKey="neutral" stackId="1" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.6} name="Neutral" dot={true} />
                <Area type="monotone" dataKey="negative" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Negative" dot={true} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Four Cards Row: Average Rating, Sentiment Analysis, Review Sources, Google Places */}
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        
        {/* Google Reviews Card */}
        <GoogleReviewCard 
          business={businessDetails}
          showGrowthMetrics={true}
          compact={false}
        />
        
        {/* Average Rating Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <CardDescription>Overall customer satisfaction</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col justify-center h-64 p-6 space-y-4">
              {/* Conditional rendering based on data availability */}
              {averageRating === null && totalReviews === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-xl text-gray-400">No rating data available yet.</p>
                </div>
              ) : (
                <>
                  {/* Top section: Average rating number, stars, trend, count */}
                  {averageRating !== null && (
                    <div className="flex items-center justify-center space-x-4">
                       <div className="flex flex-col items-center">
                         <div className="text-4xl font-bold flex items-center">
                           {averageRating.toFixed(1)}
                           {trend === 'up' && (
                          <svg className="w-8 h-8 text-green-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                        {trend === 'down' && (
                          <svg className="w-8 h-8 text-red-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                        {trend === 'neutral' && (
                          <svg className="w-8 h-8 text-gray-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex text-gray-400 mt-2 justify-center gap-[4%] w-full max-w-[300px] mx-auto">
                        {[...Array(5)].map((_, i) => {
                          const starIndex = i + 1;
                          const isFullStar = averageRating !== null && starIndex <= Math.floor(averageRating);
                          const isPartialStar = averageRating !== null && starIndex === Math.ceil(averageRating) && averageRating % 1 !== 0;
                          const partialFillPercentage = isPartialStar ? (averageRating % 1) * 100 : 0;

                          return (
                            <svg
                              key={i}
                              className="flex-1 min-w-6 max-w-14 aspect-square"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              style={{ fill: isFullStar ? '#f59e0b' : 'transparent', stroke: '#6b7280', strokeWidth: '1' }}
                            >
                              {isPartialStar && (
                                <linearGradient id={`partial-fill-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset={`${partialFillPercentage}%`} style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                                  <stop offset={`${partialFillPercentage}%`} style={{ stopColor: 'transparent', stopOpacity: 1 }} />
                                </linearGradient>
                              )}
                              <path
                                d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.5828 8.332-1.151z"
                                fill={isPartialStar ? `url(#partial-fill-${i})` : (isFullStar ? '#f59e0b' : 'transparent')}
                                stroke="#6b7280"
                                strokeWidth="1"
                              />
                            </svg>
                          );
                        })}
                      </div>
                         {totalReviews !== null && (
                           <div className="text-white-500 mt-2">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</div>
                         )}
                       </div>
                    </div>
                  )}
                  {/* Bottom section: Star distribution bars */}
                  {starRatingDistribution.length > 0 && (
                    <div className="flex flex-col justify-center w-full">
                      {starRatingDistribution.sort((a, b) => b.rating - a.rating).map(({ rating, count }) => {
                        const barWidth = totalReviews !== null && totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                        return (
                          <div key={rating} className="flex items-center mb-1">
                            <div className="text-sm text-gray-400 mr-2 w-6 text-right min-w-0">
                              {rating}★
                            </div>
                            <div className="flex-grow rounded-full h-2.5 relative" style={{ backgroundColor: 'transparent', outline: '1px solid grey' }}>
                              <div
                                  className={`h-2.5 rounded-full absolute top-0 left-0`}
                                  style={{ width: `${barWidth}%`, backgroundColor: '#f59e0b' }}
                                ></div>
                            </div>
                            <div className="text-sm text-gray-400 ml-2 w-8 text-left min-w-0">
                              ({count})
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </CardContent>
        </Card>

        {/* Sentiment Analysis Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Sentiment Analysis</CardTitle>
            <CardDescription>Distribution of customer sentiment</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sentimentAnalysisData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius="75%"
                    fill="#8884d8"
                    dataKey="value"
                  >
                  {sentimentAnalysisData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    formatter={(value, entry) => `${entry.payload.name}: ${entry.payload.value} (${((entry.payload.value / sentimentAnalysisData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}%)`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Review Sources Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Review Sources</CardTitle>
            <CardDescription>Where your reviews are coming from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={reviewSourcesData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius="75%"
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {reviewSourcesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    formatter={(value, entry) => `${entry.payload.name}: ${entry.payload.value} (${((entry.payload.value / reviewSourcesData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}%)`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default ReviewInsights;
